import React from 'react';

import { Modal } from 'src/components/Modal/Modal';
import {
  ActivityLog,
  ThirdPartyLog,
  formatActorTypeLabel,
  formatDurationMs,
  formatProviderLabel,
  formatSourceLabel,
  statusTone,
} from 'src/api/adminLogs';
import { convertDate } from 'src/components/Table/helpers';

import {
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailSection,
  DetailValue,
  MetaBlock,
  ModalTitle,
  StatusCode,
} from './styled';

type ActivityDetailModalProps = {
  kind: 'activity';
  log: ActivityLog;
  onClose: () => void;
};

type ThirdPartyDetailModalProps = {
  kind: 'third-party';
  log: ThirdPartyLog;
  onClose: () => void;
};

type LogDetailModalProps = ActivityDetailModalProps | ThirdPartyDetailModalProps;

const prettyJson = (value: unknown) => {
  if (value == null) return '—';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/** Excerpts are stored as JSON strings (first 2KB): indent them when they parse, else show as-is. */
const prettyExcerpt = (raw: string) => {
  try {
    return prettyJson(JSON.parse(raw));
  } catch {
    return raw;
  }
};

const LogDetailModal: React.FC<LogDetailModalProps> = (props) => {
  if (props.kind === 'activity') {
    const { log, onClose } = props;
    const when = log.created_at ? convertDate(log.created_at) : null;
    const tone = statusTone(log.status);

    return (
      <Modal isOpen onClose={onClose}>
        <DetailLabel>Activity</DetailLabel>
        <ModalTitle>{log.action || '—'}</ModalTitle>
        <DetailGrid>
          <DetailItem>
            <DetailLabel>Time</DetailLabel>
            <DetailValue>{when ? `${when.date} ${when.time}` : '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Status</DetailLabel>
            <DetailValue>
              <StatusCode $tone={tone}>{log.status ?? '—'}</StatusCode>
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Actor</DetailLabel>
            <DetailValue>
              {log.actor_email || formatActorTypeLabel(log.actor_type)}
              {log.actor ? ` (${log.actor})` : ''}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Duration</DetailLabel>
            <DetailValue>{formatDurationMs(log.duration_ms)}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Source</DetailLabel>
            <DetailValue>
              {formatSourceLabel(log.source)}
              {log.host ? ` · ${log.host}` : ''}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Path</DetailLabel>
            <DetailValue>{log.path || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>IP</DetailLabel>
            <DetailValue>{log.ip || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>User agent</DetailLabel>
            <DetailValue>{log.user_agent || '—'}</DetailValue>
          </DetailItem>
        </DetailGrid>
        <DetailSection>
          <DetailLabel>Meta</DetailLabel>
          <MetaBlock>{prettyJson(log.meta)}</MetaBlock>
        </DetailSection>
      </Modal>
    );
  }

  const { log, onClose } = props;
  const when = log.created_at ? convertDate(log.created_at) : null;
  const tone = statusTone(log.status_code);
  const call = log.url || log.context || '—';

  return (
    <Modal isOpen onClose={onClose}>
      <DetailLabel>Third-party call</DetailLabel>
      <ModalTitle>{formatProviderLabel(log.provider)}</ModalTitle>
      <DetailGrid>
        <DetailItem>
          <DetailLabel>Time</DetailLabel>
          <DetailValue>{when ? `${when.date} ${when.time}` : '—'}</DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Status</DetailLabel>
          <DetailValue>
            <StatusCode $tone={tone}>{log.status_code ?? '—'}</StatusCode>
          </DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Call</DetailLabel>
          <DetailValue>
            {log.method ? `${log.method} ` : ''}
            {call}
          </DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Duration</DetailLabel>
          <DetailValue>{formatDurationMs(log.duration_ms)}</DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Source</DetailLabel>
          <DetailValue>
            {formatSourceLabel(log.source)}
            {log.host ? ` · ${log.host}` : ''}
          </DetailValue>
        </DetailItem>
        <DetailItem>
          <DetailLabel>Result</DetailLabel>
          <DetailValue>{log.ok ? 'OK' : log.error || 'Failed'}</DetailValue>
        </DetailItem>
      </DetailGrid>
      {log.request_excerpt && (
        <DetailSection>
          <DetailLabel>Request payload (what we sent)</DetailLabel>
          <MetaBlock>{prettyExcerpt(log.request_excerpt)}</MetaBlock>
        </DetailSection>
      )}
      {log.response_excerpt && (
        <DetailSection>
          <DetailLabel>Response excerpt</DetailLabel>
          <MetaBlock>{prettyExcerpt(log.response_excerpt)}</MetaBlock>
        </DetailSection>
      )}
    </Modal>
  );
};

export default LogDetailModal;
